import React, { useState, useEffect, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";

const statStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  fontSize: 18,
  fontWeight: 600,
  textAlign: "left",
  paddingLeft: 18,
  marginBottom: 10,
  width: 270,
  height: 51,
  background: "rgba(95, 49, 18, 0.68)",
  border: "2px solid rgba(70, 32, 10, 0.95)",
  borderRadius: "6px",

  textShadow: `
    -1px -1px 0 #000,
     1px -1px 0 #000,
    -1px  1px 0 #000,
     1px  1px 0 #000,
     0px  1px 0 #000,
     0px  1px 1px rgba(0, 0, 0, 0.75)
  `,

  boxShadow: `
    inset 0 1px 0 rgba(255, 255, 255, 0.18),
    inset 0 -2px 0 rgba(0, 0, 0, 0.18),
    0 2px 0 rgba(0, 0, 0, 0.18)
  `,

  fontFamily: '"Fredoka", sans-serif',
};

const iconBox = {
  height: "32px",
  width: "32px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginRight: 6
};

const statStyle2 = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  fontSize: 15,
  width: 111,
  height: 111,
  backgroundImage: "url('/v2/item.png')",
  backgroundSize: "111px 111px",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "center",
  gap: 3
};

const statGrid = {
  fontFamily: '"Fredoka", sans-serif',
  fontWeight: 600,
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "center",
  gap: 15,
  maxWidth: 242
};

export default function SeasonChangeModal({
  onClose,
  user,
  setUser,
  mode,
  onNavigate,  
}) {
  const [airdropStats, setAirdropStats] = useState(null);
  const [loadingAirdropStats, setLoadingAirdropStats] = useState(true);
  const [airdropStatsError, setAirdropStatsError] = useState("");
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const token = localStorage.getItem("token");

    if (!token) {
      setAirdropStatsError("Missing token");
      setLoadingAirdropStats(false);
      return;
    }

    fetch(`${process.env.REACT_APP_API_URL}/api/airdrops`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(async (res) => {
        const text = await res.text();
        const data = JSON.parse(text);

        console.log(data);

        if (!res.ok) {
          throw new Error(data?.error || "Failed to load airdrop stats");
        }

        setAirdropStats(data);
        setAirdropStatsError("");

      })
      .catch((err) => {
        console.error("Failed to load airdrop stats:", err);
        setAirdropStats(null);
        setAirdropStatsError(err.message || "Failed to load airdrop stats");
      })
      .finally(() => {
        setLoadingAirdropStats(false);
      });
  }, []);

  const sparksEarned = airdropStats?.stats?.progression?.sparksEarned;
  const runesEarned = airdropStats?.stats?.progression?.runesEarned;
  const finalLevel = airdropStats?.stats?.progression?.characterLevel;
  const totalBattles = airdropStats?.stats?.combat?.totalBattles;
  const totalWins = airdropStats?.stats?.combat?.wins;
  const totalCards = airdropStats?.stats?.cardAssets?.totalCards;
  const totalAirdropPoints = airdropStats?.totals?.overall;

  const starterPack = useMemo(() => {
  if (airdropStats?.starterPack) return airdropStats.starterPack;

  const rows = Array.isArray(airdropStats?.config?.starterPack)
    ? [...airdropStats.config.starterPack]
    : [];

  rows.sort((a, b) => {
    if ((Number(a?.pointsStart) || 0) !== (Number(b?.pointsStart) || 0)) {
      return (Number(a?.pointsStart) || 0) - (Number(b?.pointsStart) || 0);
    }
    return (Number(a?.pointsEnd) || 0) - (Number(b?.pointsEnd) || 0);
  });

  return rows[0] || null;
}, [airdropStats]);
  
  const starterPackSparks = starterPack?.sparksAmount ?? 0;
const starterPackRunes = starterPack?.runesAmount ?? 0;
const starterPackTapi = starterPack?.tapiAmount ?? 0;

const starterPackCard1 = starterPack?.card1 ?? "none";
const starterPackCard2 = starterPack?.card2 ?? "none";

  const byItem = airdropStats?.totals?.byItem;
  const sparksPoints = byItem?.progression?.sparksEarned;
  const runesPoints = byItem?.progression?.runesEarned;
  const levelPoints = byItem?.progression?.characterLevel;
  const tasksPoints = byItem?.progression?.tasksCompleted;
  const commonCardPoints = byItem?.cardAssets?.commonCard;
  const rareCardPoints = byItem?.cardAssets?.rareCard;
  const epicCardPoints = byItem?.cardAssets?.epicCard;
  const legendaryCardPoints = byItem?.cardAssets?.legendaryCard;
  const winsPoints = byItem?.combat?.wins;
  const trophiesPoints = byItem?.progression?.trophies ?? 0;
  const tgStarsPoints = byItem?.investment?.tgStars;
  const gamepassPoints = byItem?.investment?.gamepass;
  const referrersPoints = byItem?.social?.referrers;

  const globalStats = airdropStats?.globalStats || {};
  const globalTotalSparks = globalStats?.totalSparks ?? 0;
  const globalTotalRunes = globalStats?.totalRunes ?? 0;
  const globalTotalBattles = globalStats?.totalBattles ?? 0;
  const globalTotalPlayers = globalStats?.totalPlayers ?? 0;

  const [claimingStarterPack, setClaimingStarterPack] = useState(false);
  const [claimStarterPackError, setClaimStarterPackError] = useState("");
  const [claimStarterPackSuccess, setClaimStarterPackSuccess] = useState("");

  const starterPackClaim = airdropStats?.starterPackClaim || null;
  const starterPackAlreadyClaimed = !!starterPackClaim?.claimed;
  const starterPackClaimedAt = starterPackClaim?.claimedAt ?? null;

  const claimStartsAtMs = parseDateMs(airdropStats?.claimStartsAt);
const claimEndsAtMs = parseDateMs(airdropStats?.claimEndsAt);

const [claimingAirdrop, setClaimingAirdrop] = useState(false);
const [claimAirdropError, setClaimAirdropError] = useState("");
const [claimAirdropSuccess, setClaimAirdropSuccess] = useState("");

const airdropClaim = airdropStats?.airdropClaim || null;
const airdropAlreadyClaimed = !!airdropClaim?.claimed;

const [nowMs, setNowMs] = useState(Date.now());

    useEffect(() => {
      const id = setInterval(() => {
        setNowMs(Date.now());
      }, 1000);

      return () => clearInterval(id);
    }, []);

    function parseDateMs(value) {
      if (!value) return null;
      const ms = new Date(value).getTime();
      return Number.isFinite(ms) ? ms : null;
    }

    function formatCountdown(ms) {
      const total = Math.max(0, Math.floor(ms / 1000));
      const days = Math.floor(total / 86400);
      const hours = Math.floor((total % 86400) / 3600);
      const minutes = Math.floor((total % 3600) / 60);
      const seconds = total % 60;

      if (days > 0) {
        return `${days}d ${String(hours).padStart(2, "0")}h ${String(minutes).padStart(2, "0")}m`;
      }

      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    }


const claimNotStarted =
  claimStartsAtMs != null && nowMs < claimStartsAtMs;

const claimEnded =
  claimEndsAtMs != null && nowMs > claimEndsAtMs;

const claimLive =
  !claimNotStarted && !claimEnded;

const claimStartsInMs =
  claimNotStarted && claimStartsAtMs != null
    ? Math.max(0, claimStartsAtMs - nowMs)
    : 0;

const claimEndsInMs =
  claimLive && claimEndsAtMs != null
    ? Math.max(0, claimEndsAtMs - nowMs)
    : 0;

const lastPageButtonText =
  claimNotStarted
    ? `Claim Starts In ${formatCountdown(claimStartsInMs)}`
    : claimEnded
    ? "Claim Ended"
    : claimEndsAtMs != null
    ? `Claim Ends In ${formatCountdown(claimEndsInMs)}`
    : "Claim Live";

const lastPageSubtext =
  claimNotStarted
    ? "The airdrop claim window has not opened yet."
    : claimEnded
    ? "The airdrop claim window is closed."
    : claimEndsAtMs != null
    ? `You can claim until ${new Date(claimEndsAtMs).toLocaleString()}.`
    : "Your airdrop claim is available now.";




  async function handleClaimStarterPack() {
    if (claimingStarterPack) return;
    if (starterPackAlreadyClaimed) {
      next();
      return;
    }

    const token = localStorage.getItem("token");

    if (!token) {
      setClaimStarterPackError("Missing token");
      return;
    }

    setClaimingStarterPack(true);
    setClaimStarterPackError("");
    setClaimStarterPackSuccess("");

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/airdrops/starter-pack/claim`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const text = await res.text();

      let data = {};

      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        data = {};
      }

  if (!res.ok) {
  if (res.status === 409) {
    setAirdropStats((prev) => ({
      ...prev,
      starterPackClaim: {
        claimed: true,
        claimedAt: data?.claimedAt || new Date().toISOString(),
      },
    }));

    setClaimStarterPackSuccess("Starter Pack already claimed.");
    next();
    return;
  }

  throw new Error(
    data?.error ||
    text ||
    "Failed to claim starter pack"
  );
}

      const rewardSparks = Number(data?.rewards?.sparksAmount || 0);
const rewardRunes = Number(data?.rewards?.runesAmount || 0);
const rewardTapi = Number(data?.rewards?.tapiAmount || 0);

setUser?.((u) => ({
  ...(u || {}),
  sparks: Number(u?.sparks || 0) + rewardSparks,
  runes: Number(u?.runes || 0) + rewardRunes,
  tapi: Number(u?.tapi || 0) + rewardTapi,
}));

setAirdropStats((prev) => ({
  ...prev,
  user: {
    ...prev?.user,
    sparks:
      Number(prev?.user?.sparks || 0) + rewardSparks,
    runes:
      Number(prev?.user?.runes || 0) + rewardRunes,
    tapi:
      Number(prev?.user?.tapi || 0) + rewardTapi,
  },
  starterPackClaim: {
    claimed: true,
    claimedAt: data?.claimedAt || new Date().toISOString(),
  },
}));

      setClaimStarterPackSuccess("Starter Pack claimed successfully!");
      next();
    } catch (err) {
      console.error("Failed to claim starter pack:", err);
      setClaimStarterPackError(err.message || "Failed to claim starter pack");
    } finally {
      setClaimingStarterPack(false);
    }
  }

  async function handleClaimAirdrop() {
  if (claimingAirdrop) return;

  const token = localStorage.getItem("token");

  if (!token) {
    setClaimAirdropError("Missing token");
    return;
  }

  setClaimingAirdrop(true);
  setClaimAirdropError("");
  setClaimAirdropSuccess("");

  try {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/airdrops/claim`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    const text = await res.text();
    const data = text ? JSON.parse(text) : {};

  if (!res.ok) {
  throw new Error(
    data?.error ||
    "Failed to claim airdrop"
  );
}

const claimedAmount = Number(data?.claimedAmount || 0);
const finalTapiBalance = Number(data?.tapi ?? claimedAmount);

setUser?.((prev) => ({
  ...(prev || {}),
  tapi: finalTapiBalance,
  hasUnclaimedAirdrop: false,
}));
    
setAirdropStats((prev) => ({
  ...(prev || {}),
  airdropClaim: {
    claimed: true,
    claimedAt: data?.claimedAt || new Date().toISOString(),
  },
}));

setClaimAirdropSuccess("Airdrop claimed successfully!");

  } catch (err) {
    console.error("Claim airdrop failed:", err);
    setClaimAirdropError(err.message || "Failed to claim airdrop");
  } finally {
    setClaimingAirdrop(false);
  }
}

  function fmt(value) {
    if (value === null || value === undefined) return "—";
    return new Intl.NumberFormat("en").format(Number(value) || 0);
  }

  function fmtCompact(value) {
    if (value === null || value === undefined) return "—";
    return new Intl.NumberFormat("en", {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(Number(value) || 0);
  }

  function getStarterPackCardMeta(card) {
    const value = String(card || "none").toLowerCase();

    switch (value) {
      case "common":
        return { label: "Common Card", image: "/common.png" };
      case "rare":
        return { label: "Rare Card", image: "/rare.png" };
      case "epic":
        return { label: "Epic Card", image: "/epic.png" };
      case "legendary":
        return { label: "Legendary Card", image: "/legendary.png" };
      default:
        return null;
    }
  }

  const starterCard1Meta = getStarterPackCardMeta(starterPackCard1);
  const starterCard2Meta = getStarterPackCardMeta(starterPackCard2);

  const pages = [
    {
      id: 0,
      buttonText: "See My Stats",
      content: (
        <>
          <div className="text-outline-white" style={{ fontFamily: '"Fredoka", sans-serif',fontSize: 21, fontWeight: 600, marginBottom: 5 }}>
            Welcome to Season 3:
          </div>

          <div className="text-outline-yellow" style={{ fontFamily: '"Fredoka", sans-serif',fontSize: 24, fontWeight: 600, marginBottom: 25, color: "#FFF04E" }}>
            Sands of Shadow
          </div>

          <div className="text-outline-yellow" style={{ fontFamily: '"Fredoka", sans-serif',fontSize: 15, fontWeight: 600, marginBottom: 5 }}>
            A monumental journey has concluded.
          </div>

          <div className="text-outline-white" style={{ fontFamily: '"Fredoka", sans-serif', fontWeight: 600, fontSize: 21, marginBottom: 25 }}>
            Together, you’ve built an empire.
          </div>

          <div style={statStyle}>
            <div style={iconBox}>
              <img src="/sparks.png" style={{ height: 32 }} />
            </div>
            {fmtCompact(globalTotalSparks)}&nbsp;<span style={{ color: "#FFC97F" }}>Sparks Earned</span>
          </div>

          <div style={statStyle}>
            <div style={iconBox}>
              <img src="/runes.png" style={{ height: 32 }} />
            </div>
            {fmtCompact(globalTotalRunes)}&nbsp;<span style={{ color: "#FFC97F" }}>Runes Collected</span>
          </div>

          <div style={statStyle}>
            <div style={iconBox}>
              <img src="/battlem.png" style={{ height: 24 }} />
            </div>
            {fmtCompact(globalTotalBattles)}&nbsp;<span style={{ color: "#FFC97F" }}>Battles Fought</span>
          </div>

          <div style={statStyle}>
            <div style={iconBox}>
              <img src="/friends.png" style={{ height: 24 }} />
            </div>
            {fmtCompact(globalTotalPlayers)}&nbsp;<span style={{ color: "#FFC97F" }}>Players Joined</span>
          </div>
        </>
      ),
    },
    {
      id: 1,
      buttonText: "Continue",
      content: (
        <>
          <div className="text-outline-white" style={{ fontFamily: '"Fredoka", sans-serif',fontWeight: 600, fontSize: 21, marginBottom: 15 }}>
            Your Season 2 Legacy:
          </div>

          <div className="text-outline-yellow" style={{ fontSize: 14, marginBottom: 25,fontFamily: '"Fredoka", sans-serif',fontWeight: 600 }}>
            You fought like a titan. Your name is etched in
            <br />
            the history of the Eternal Scars.
          </div>

          <div style={statGrid}>
            <div style={statStyle2}>
              <img src="/sparks.png" style={{ height: 32 }} />
              <div>{fmt(sparksEarned)}</div>
              <span style={{ fontSize: 13, color: "#FFC97F" }}>Sparks Earned</span>
            </div>

            <div style={statStyle2}>
              <img src="/runes.png" style={{ height: 32 }} />
              <div>{fmt(runesEarned)}</div>
              <span style={{ fontSize: 13, color: "#FFC97F" }}>Runes Collected</span>
            </div>

            <div style={statStyle2}>
              <img src="/battlem.png" style={{ height: 28 }} />
              <div>{fmt(totalBattles)}</div>
              <span style={{ fontSize: 13, color: "#FFC97F" }}>Total Battles</span>
            </div>

            <div style={statStyle2}>
              <img src="/battlem.png" style={{ height: 28 }} />
              <div>{fmt(totalWins)}</div>
              <span style={{ fontSize: 13, color: "#FFC97F" }}>Total Wins</span>
            </div>

            <div style={statStyle2}>
              <img src="/pieces.png" style={{ height: 32 }} />
              <div>{fmt(totalCards)}</div>
              <span style={{ fontSize: 13, color: "#FFC97F" }}>Cards Owned</span>
            </div>

            <div style={statStyle2}>
              <img src="/stats.png" style={{ height: 32 }} />
              <div>{fmt(finalLevel)}</div>
              <span style={{ fontSize: 13, color: "#FFC97F" }}>Final Level</span>
            </div>
          </div>
        </>
      ),
    },
    {
      id: 2,
      buttonText: starterPackAlreadyClaimed ? "Continue" : "Claim & Continue",
      content: (
        <>
          <div className="text-outline-white" style={{ fontFamily: '"Fredoka", sans-serif',fontWeight: 600,fontSize: 24, marginBottom: 15 }}>
            A New Chapter Begins
          </div>

          <div className="text-outline-yellow" style={{ fontWeight: 600, fontFamily: '"Fredoka", sans-serif',fontSize: 18, marginBottom: 1 }}>
            Everyone starts fresh today.
          </div>

          <div className="text-outline-yellow" style={{ fontWeight: 600, fontFamily: '"Fredoka", sans-serif',fontSize: 21, marginBottom: 10 }}>
            New leaderboard, new glory.
          </div>

          <div
            className="text-outline-white"
            style={{ fontFamily: '"Fredoka", sans-serif',fontSize: 18, fontWeight: 600, marginBottom: 20 }}
          >
            {mode === "newUser"
              ? "Here Is Your Starter Pack:"
              : (
                <>
                  As a tribute to your Season 2 effort,
                  <br />
                  here is your Starter Pack:
                </>
              )}
          </div>

          <div style={statGrid}>
            <div style={statStyle2}>
              <img src="/sparks.png" style={{ height: 32 }} />
              <div>{fmt(starterPackSparks)}</div>
              <span style={{ fontSize: 16, color: "#FFC97F" }}>Sparks</span>
            </div>

            <div style={statStyle2}>
              <img src="/runes.png" style={{ height: 32 }} />
              <div>{fmt(starterPackRunes)}</div>
              <span style={{ fontSize: 16, color: "#FFC97F" }}>Runes</span>
            </div>

            {Number(starterPackTapi) > 0 && (
  <div style={statStyle2}>
    <img src="/icon_tapi.png" style={{ height: 32 }} />
    <div>{fmt(starterPackTapi)}</div>
    <span style={{ fontSize: 16, color: "#FFC97F" }}>TAPi</span>
  </div>
)}

            {starterCard1Meta && (
              <div style={statStyle2}>
                <img src={starterCard1Meta.image} style={{ height: 32 }} />
                <div>1x</div>
                <span style={{ fontSize: 16, color: "#FFC97F" }}>{starterCard1Meta.label}</span>
              </div>
            )}

            {starterCard2Meta && (
              <div style={statStyle2}>
                <img src={starterCard2Meta.image} style={{ height: 32 }} />
                <div>1x</div>
                <span style={{ fontSize: 16, color: "#FFC97F" }}>{starterCard2Meta.label}</span>
              </div>
            )}
          </div>

          {starterPackAlreadyClaimed && (
            <div
              className="text-outline-white"
              style={{ fontFamily: '"Fredoka", sans-serif',fontWeight: 600,fontSize: 15, marginTop: 18, color: "#8CFF98" }}
            >
              Starter Pack Already Claimed
            </div>
          )}

          {!!claimStarterPackSuccess && (
            <div
              className="text-outline-white"
              style={{ fontFamily: '"Fredoka", sans-serif',fontWeight: 600,fontSize: 15, marginTop: 12, color: "#8CFF98" }}
            >
              {claimStarterPackSuccess}
            </div>
          )}

          {!!claimStarterPackError && (
            <div
              className="text-outline-white"
              style={{ fontFamily: '"Fredoka", sans-serif',fontWeight: 600,fontSize: 15, marginTop: 12, color: "#FF8C8C" }}
            >
              {claimStarterPackError}
            </div>
          )}
        </>
      ),
    },
    {
      id: 3,
      buttonText: "See Your Airdrop",
      content: (
        <>
          <div className="text-outline-white" style={{ fontSize: 28, fontWeight: 600, marginBottom: 15,fontFamily: '"Fredoka", sans-serif' }}>
            You're On The List
          </div>

          <div style={{ position: "relative", width: 300, height: 200 }}>
            <img src="/f1.png" style={{ zIndex: "1", left: 0, position: "absolute", top: "30%", width: "100%" }} />
            <img src="/list.png" style={{ position: "relative", zIndex: "2", width: "214px", height: "214px" }} />
          </div>

          <div className="text-outline-yellow" style={{ fontFamily: '"Fredoka", sans-serif',fontWeight: 600, fontSize: 42, marginBottom: 1, marginTop: 15 }}>
            {totalAirdropPoints}
          </div>
          <div className="text-outline-white" style={{ fontFamily: '"Fredoka", sans-serif',fontWeight: 600, fontSize: 24 }}>TAPI Points</div>
        </>
      ),
    },
    {
      id: 4,
      buttonText: "CLAIMS SOON",
      content: (
        <>
          <div className="text-outline-white" style={{ fontFamily: '"Fredoka", sans-serif',fontSize: 21, fontWeight: 600, marginBottom: 5 }}>
            Season 2 Rewards
          </div>

          <div className="text-outline-white" style={{ fontFamily: '"Fredoka", sans-serif',fontWeight: 600, fontSize: 28 }}>
            Your Total Airdrop
          </div>

          <div className="text-outline-yellow" style={{ color: "#FFE100", fontFamily: '"Fredoka", sans-serif',fontWeight: 600, fontSize: 28, marginBottom: 1, marginTop: 10 }}>
            {totalAirdropPoints} Tapi Points
          </div>
          <div
  className="text-outline-white"
  style={{
    fontFamily: '"Fredoka", sans-serif',
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 15,
    marginTop: 5,
    color: claimNotStarted ? "#FFD66B" : claimEnded ? "#FF8C8C" : "#8CFF98",
  }}
>
  {lastPageSubtext}
</div>

<div className="modal_title">Your $TAPI Allocation</div>
<div className="modal_body modal_body_2" style={{marginBottom: 0, paddingTop: 15, maxHeight: 280, overflow: "auto"}}>

<div className="nomodal" style={{float: "left"}}>
<div className="info" style={{display: "flex", justifyContent: "space-between" }}>
              Total Sparks Earned <span style={{ color: "#FFE100" }}>{sparksPoints}</span>
            </div>
            <div className="info" style={{display: "flex", justifyContent: "space-between" }}>
              Total Runes Earned <span style={{ color: "#FFE100" }}>{runesPoints}</span>
            </div>
            <div
  className="info"
  style={{ display: "flex", justifyContent: "space-between" }}
>
  Trophies
  <span style={{ color: "#FFE100" }}>
    {trophiesPoints}
  </span>
</div>
            <div className="info" style={{display: "flex", justifyContent: "space-between" }}>
              Character Level <span style={{ color: "#FFE100" }}>{levelPoints}</span>
            </div>
            <div className="info" style={{display: "flex", justifyContent: "space-between" }}>
              Tasks Completed <span style={{ color: "#FFE100" }}>{tasksPoints}</span>
            </div>
            <div className="info" style={{display: "flex", justifyContent: "space-between" }}>
              Common Cards <span style={{ color: "#FFE100" }}>{commonCardPoints}</span>
            </div>
            <div className="info" style={{display: "flex", justifyContent: "space-between" }}>
              Rare Cards <span style={{ color: "#FFE100" }}>{rareCardPoints}</span>
            </div>
            <div className="info" style={{display: "flex", justifyContent: "space-between" }}>
              Epic Cards <span style={{ color: "#FFE100" }}>{epicCardPoints}</span>
            </div>
            <div className="info" style={{display: "flex", justifyContent: "space-between" }}>
              Legendary Cards <span style={{ color: "#FFE100" }}>{legendaryCardPoints}</span>
            </div>
            <div className="info" style={{display: "flex", justifyContent: "space-between" }}>
              Battle Performance <span style={{ color: "#FFE100" }}>{winsPoints}</span>
            </div>
            <div className="info" style={{display: "flex", justifyContent: "space-between" }}>
              TG Stars Purchases <span style={{ color: "#FFE100" }}>{tgStarsPoints}</span>
            </div>
            <div className="info" style={{display: "flex", justifyContent: "space-between" }}>
              Referrers <span style={{ color: "#FFE100" }}>{referrersPoints}</span>
            </div>
            <div className="info" style={{display: "flex", justifyContent: "space-between" }}>
              GamePass <span style={{ color: "#FFE100" }}>{gamepassPoints}</span>
            </div></div>


</div>
          
          
        </>
      ),
    },
  ];

  const pageMapByMode = {
  all: [0, 1, 2, 3, 4],
  newUser: [2],
  legacy: [0, 1, 2],
  airdrop: [0, 1, 2, 3, 4],
};

  const visiblePageIndexes = pageMapByMode[mode] || pageMapByMode.all;

  const visiblePages = useMemo(() => {
    return pages.filter((page) => visiblePageIndexes.includes(page.id));
  }, [visiblePageIndexes]);

  const [page, setPage] = useState(0);

  if (loadingAirdropStats) {
    return (
      <div className="season-modal">
        <div className="season-content">Loading stats...</div>
      </div>
    );
  }

  if (airdropStatsError || !airdropStats) {
    return (
      <div className="season-modal">
        <div className="season-content">
          Failed to load stats: {airdropStatsError || "Unknown error"}
        </div>
      </div>
    );
  }

  if (!visiblePages.length) {
    return null;
  }

  const next = () => {
    if (page === visiblePages.length - 1) {
      onClose();
    } else {
      setPage((p) => p + 1);
    }
  };

  const current = visiblePages[page];
  const isLastPage = page === visiblePages.length - 1;

  return (
    <div
      className="season-modal"
      style={{
        backgroundImage: `
          url('/v2/main_bg.png')
        `,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
      }}
    >
      {mode !== "newUser" && page >= Math.max(0, visiblePages.length - 2) && (
        <div
          onClick={onClose}
          style={{
            position: "fixed",
            top: "calc(10px + var(--tg-safe-top))",
            right: 10,
            cursor: "pointer",
            zIndex: 9999
          }}
        >
          <img src="/v2/close.png" />
        </div>
      )}

      <AnimatePresence mode="wait">
        <motion.div
          key={current.id}
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -40 }}
          transition={{ duration: 0.25 }}
          className="season-modal-page"
        >
          <div className="season-content">{current.content}</div>

          <button
          className="game-button green-button"
  onClick={
  current.id === 2
    ? handleClaimStarterPack
    : current.id === 4
    ? claimLive && !airdropAlreadyClaimed
      ? handleClaimAirdrop
      : undefined
    : next
}
  disabled={
  (current.id === 2 && claimingStarterPack) ||
  (current.id === 4 && (claimingAirdrop || !claimLive || airdropAlreadyClaimed))
}
            style={{
              fontFamily: '"Fredoka", sans-serif',
              fontSize: 21,
              padding: "0px 15px",
              marginBottom: 5,
  opacity:
    (current.id === 4 && (claimingAirdrop || !claimLive || airdropAlreadyClaimed)) ||
    (current.id === 2 && claimingStarterPack)
      ? 0.7
      : 1,
  cursor:
    (current.id === 4 && (claimingAirdrop || !claimLive || airdropAlreadyClaimed)) ||
    (current.id === 2 && claimingStarterPack)
      ? "not-allowed"
      : "pointer",
}}
          >
          {current.id === 4
  ? claimingAirdrop
    ? "Claiming..."
    : airdropAlreadyClaimed
    ? "Claimed"
    : claimLive
    ? "Claim Now"
    : claimEnded
    ? "Claim Ended"
    : "Claim Soon"
  : current.id === 2
  ? claimingStarterPack
    ? "Claiming..."
    : starterPackAlreadyClaimed
    ? "Continue"
    : current.buttonText
  : isLastPage
  ? "Close"
  : current.buttonText}
          </button>

{current.id === 4 && !airdropAlreadyClaimed && (
  <div
    className="text-outline-white"
    style={{
      marginTop: 8,
      fontSize: 16,
      fontWeight: 600,
      textAlign: "center",
      fontFamily: '"Fredoka", sans-serif',
      color: claimNotStarted ? "#FFD66B" : claimEnded ? "#FF8C8C" : "#8CFF98",
      minHeight: 20,
    }}
  >
    {claimNotStarted
      ? `Claim Starts In ${formatCountdown(claimStartsInMs)}`
      : claimEnded
      ? "Claim Window Ended"
      : claimEndsAtMs != null
      ? `Claim Ends In ${formatCountdown(claimEndsInMs)}`
      : "Claim Is Live Now"}
  </div>
)}

        </motion.div>
      </AnimatePresence>
    </div>
  );
}